/**
 * Content Item Migration Utilities
 *
 * Migrates existing Note and CalendarEvent data to the unified ContentItem model.
 * Ensures backward compatibility during the transition period.
 */

import { contentItemsDB, type Note, type CalendarEvent, type ContentItem } from '../db';

/**
 * Convert a Note to a ContentItem
 */
export function noteToContentItem(note: Note, spaceId?: string): ContentItem {
  return {
    id: note.id,
    spaceId: spaceId || note.spaceId,
    title: note.title,
    content: note.content,

    // Notes don't have completion by default
    completed: undefined,
    completedAt: undefined,
    completedBy: undefined,

    // No dates for basic notes
    dueDate: undefined,
    startDate: undefined,
    endDate: undefined,
    isAllDay: undefined,

    // Organization
    tags: note.tags || [],
    priority: undefined,
    isPinned: note.isPinned,
    isArchived: note.isArchived,
    order: undefined,

    // Metadata (preserve category if exists)
    metadata: note.category ? { category: note.category } : {},

    // Relations
    parentId: undefined,

    // Drive & Sync
    driveFileId: note.driveFileId,
    syncStatus: note.syncStatus,
    syncError: note.syncError,

    // Timestamps
    createdAt: note.createdAt,
    updatedAt: note.updatedAt,
    createdBy: note.createdBy,
    lastEditedBy: note.lastEditedBy,
  };
}

/**
 * Convert a CalendarEvent to a ContentItem
 */
export function calendarEventToContentItem(event: CalendarEvent, spaceId?: string): ContentItem {
  return {
    id: event.id,
    spaceId: spaceId || event.spaceId,
    title: event.title,
    content: event.description,

    // Events don't have completion
    completed: undefined,
    completedAt: undefined,
    completedBy: undefined,

    // Date fields from calendar event (note: CalendarEvent uses startTime/endTime)
    dueDate: event.startTime, // Use startTime as dueDate for compatibility
    startDate: event.startTime,
    endDate: event.endTime,
    isAllDay: event.isAllDay,

    // Organization
    tags: [],
    priority: undefined,
    isPinned: undefined,
    isArchived: undefined,
    order: undefined,

    // Metadata (store event-specific data)
    metadata: {
      location: event.location,
      color: event.color,
      category: event.category,
      rsvpEnabled: event.rsvpEnabled,
      rsvpList: event.rsvpList,
      status: event.status,
      visibility: event.visibility,
    },

    // Relations
    parentId: undefined,

    // Drive & Sync
    driveFileId: event.driveFileId,
    syncStatus: event.syncStatus,
    syncError: event.syncError,

    // Google Calendar Integration
    syncToGoogleCalendar: event.syncToGoogleCalendar,
    googleCalendarEventId: event.googleCalendarEventId,
    googleCalendarSyncStatus: event.googleCalendarSyncStatus,
    googleCalendarSyncError: event.googleCalendarSyncError,
    lastGoogleCalendarSync: event.lastGoogleCalendarSync,

    // Timestamps
    createdAt: event.createdAt,
    updatedAt: event.updatedAt,
    createdBy: event.createdBy,
    lastEditedBy: event.createdBy, // Events don't track lastEditedBy separately
  };
}

/**
 * Convert a ContentItem back to a Note
 * (For backward compatibility)
 */
export function contentItemToNote(item: ContentItem): Note {
  return {
    id: item.id,
    spaceId: item.spaceId,
    title: item.title,
    content: item.content || '',
    tags: item.tags || [],
    category: item.metadata?.category,
    isPinned: item.isPinned || false,
    isArchived: item.isArchived || false,
    driveFileId: item.driveFileId,
    syncStatus: item.syncStatus,
    syncError: item.syncError,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    createdBy: item.createdBy,
    lastEditedBy: item.lastEditedBy,
  };
}

/**
 * Convert a ContentItem back to a CalendarEvent
 * (For backward compatibility)
 */
export function contentItemToCalendarEvent(item: ContentItem): CalendarEvent {
  return {
    id: item.id,
    spaceId: item.spaceId,
    title: item.title,
    description: item.content,
    startTime: item.startDate || item.dueDate || new Date().toISOString(),
    endTime: item.endDate || item.startDate || new Date().toISOString(),
    isAllDay: item.isAllDay || false,
    location: item.metadata?.location,
    color: item.metadata?.color,
    category: item.metadata?.category,
    rsvpEnabled: item.metadata?.rsvpEnabled || false,
    rsvpList: item.metadata?.rsvpList || [],
    status: item.metadata?.status || 'confirmed',
    visibility: item.metadata?.visibility || 'private',
    driveFileId: item.driveFileId,
    syncStatus: item.syncStatus,
    syncError: item.syncError,
    syncToGoogleCalendar: item.syncToGoogleCalendar || false,
    googleCalendarEventId: item.googleCalendarEventId,
    googleCalendarSyncStatus: item.googleCalendarSyncStatus,
    googleCalendarSyncError: item.googleCalendarSyncError,
    lastGoogleCalendarSync: item.lastGoogleCalendarSync,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    createdBy: item.createdBy,
  };
}

/**
 * Migrate all existing Notes to ContentItems
 * This is a one-time migration that should be run on app startup
 */
export async function migrateNotesToContentItems(): Promise<{
  success: boolean;
  migrated: number;
  errors: number;
}> {
  try {
    // Get all notes from all spaces
    const { db } = await import('../db');
    const notes = await db.notes.toArray();

    let migrated = 0;
    let errors = 0;

    console.log(`[Migration] Starting migration of ${notes.length} notes to ContentItems...`);

    for (const note of notes) {
      try {
        const contentItem = noteToContentItem(note);
        await contentItemsDB.create(contentItem);
        migrated++;
      } catch (error) {
        console.error(`[Migration] Failed to migrate note ${note.id}:`, error);
        errors++;
      }
    }

    console.log(`[Migration] Notes migration complete: ${migrated} migrated, ${errors} errors`);

    return { success: errors === 0, migrated, errors };
  } catch (error) {
    console.error('[Migration] Notes migration failed:', error);
    return { success: false, migrated: 0, errors: -1 };
  }
}

/**
 * Migrate all existing CalendarEvents to ContentItems
 * This is a one-time migration that should be run on app startup
 */
export async function migrateCalendarEventsToContentItems(): Promise<{
  success: boolean;
  migrated: number;
  errors: number;
}> {
  try {
    // Get all events from all spaces
    const { db } = await import('../db');
    const events = await db.events.toArray();

    let migrated = 0;
    let errors = 0;

    console.log(`[Migration] Starting migration of ${events.length} calendar events to ContentItems...`);

    for (const event of events) {
      try {
        const contentItem = calendarEventToContentItem(event);
        await contentItemsDB.create(contentItem);
        migrated++;
      } catch (error) {
        console.error(`[Migration] Failed to migrate event ${event.id}:`, error);
        errors++;
      }
    }

    console.log(`[Migration] Calendar events migration complete: ${migrated} migrated, ${errors} errors`);

    return { success: errors === 0, migrated, errors };
  } catch (error) {
    console.error('[Migration] Calendar events migration failed:', error);
    return { success: false, migrated: 0, errors: -1 };
  }
}

/**
 * Check if migrations have been run
 * Stores migration status in localStorage
 */
export function getMigrationStatus(): {
  notesCompleted: boolean;
  calendarCompleted: boolean;
  lastRun?: string;
} {
  const status = localStorage.getItem('contentItemMigrationStatus');
  if (!status) {
    return { notesCompleted: false, calendarCompleted: false };
  }

  try {
    return JSON.parse(status);
  } catch {
    return { notesCompleted: false, calendarCompleted: false };
  }
}

/**
 * Mark migration as completed
 */
export function setMigrationStatus(status: {
  notesCompleted: boolean;
  calendarCompleted: boolean;
}) {
  localStorage.setItem(
    'contentItemMigrationStatus',
    JSON.stringify({
      ...status,
      lastRun: new Date().toISOString(),
    })
  );
}

/**
 * Run all migrations
 * Should be called once on app initialization
 */
export async function runMigrations(): Promise<void> {
  const status = getMigrationStatus();

  if (!status.notesCompleted) {
    console.log('[Migration] Running notes migration...');
    const result = await migrateNotesToContentItems();
    if (result.success) {
      setMigrationStatus({ ...status, notesCompleted: true });
      console.log('[Migration] Notes migration completed successfully');
    } else {
      console.warn('[Migration] Notes migration completed with errors');
    }
  } else {
    console.log('[Migration] Notes migration already completed, skipping...');
  }

  if (!status.calendarCompleted) {
    console.log('[Migration] Running calendar events migration...');
    const result = await migrateCalendarEventsToContentItems();
    if (result.success) {
      setMigrationStatus({ ...status, calendarCompleted: true });
      console.log('[Migration] Calendar events migration completed successfully');
    } else {
      console.warn('[Migration] Calendar events migration completed with errors');
    }
  } else {
    console.log('[Migration] Calendar events migration already completed, skipping...');
  }
}
